import { usePathname } from 'expo-router';
import { Menu, Search, X } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, StyleSheet, type ViewStyle } from 'react-native';
import { getTabLabelKey } from '../../config/navigation';
import { useLayoutChrome } from '../../contexts/LayoutChromeContext';
import { useTabsNavigation } from '../../contexts/TabsNavigationContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { NotificationFeedSheet } from '../../features/notifications/components/NotificationFeedSheet';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useResponsive } from '../../hooks/useResponsive';
import { isHeaderSearchEnabled } from '../../lib/featureFlags';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../theme/typography';
import { Header, HStack, Text } from '../primitives';
import { SearchInput } from '../primitives/SearchInput';
import { type RouteOverride, resolvePageTitle } from './breadcrumbs';
import { MobileSearchOverlay, NotificationBell, WorkspaceAvatar } from './Topbar';

const ICON_SIZE = 36;
const INLINE_SEARCH_WIDTH = 320;

function HeaderTitle() {
  const pathname = usePathname();
  const { t } = useTranslation();
  const tabsNav = useTabsNavigation();
  const activeTabRouteName = tabsNav?.activeTabRouteName;
  const overrides = useMemo<Record<string, RouteOverride>>(
    () => ({
      lead: { label: t('layout.nav.leads'), href: '/leads' },
    }),
    [t],
  );
  const title = useMemo(() => {
    // Prefer the focused tab's label — the pathname can be stale after a
    // state-preserving tab jump, leaving the title on the previous tab.
    const tabLabelKey = activeTabRouteName ? getTabLabelKey(activeTabRouteName) : null;
    if (tabLabelKey) return t(tabLabelKey);
    return resolvePageTitle(pathname || '/', overrides, t);
  }, [activeTabRouteName, pathname, overrides, t]);

  return (
    <Text style={styles.title} numberOfLines={1}>
      {title}
    </Text>
  );
}

const SEARCH_OPEN_MS = 300;
const SEARCH_CLOSE_MS = 200;
const SEARCH_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const isWeb = Platform.OS === 'web';

// Desktop search: a field that expands horizontally out of the magnifier,
// growing leftward and pushing the workspace switcher left. The clip pins its
// fixed-width field to the right edge so the reveal runs magnifier -> left.
// On web the width/opacity animate via CSS transitions (browser-driven — no
// per-frame JS interpolation); native falls back to the Animated driver.
function InlineExpandingSearch() {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false); // web: CSS transition target
  const [value, setValue] = useState('');
  const expand = useRef(new Animated.Value(0)).current; // native driver

  useEffect(() => {
    if (open) {
      setMounted(true);
      if (isWeb) {
        // Flip the CSS target on the next frame so the transition runs from 0.
        const id = requestAnimationFrame(() => setExpanded(true));
        return () => cancelAnimationFrame(id);
      }
      Animated.timing(expand, {
        toValue: 1,
        duration: reducedMotion ? 0 : SEARCH_OPEN_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
      return;
    }
    if (!mounted) return;
    if (isWeb) {
      setExpanded(false);
      const id = setTimeout(() => setMounted(false), reducedMotion ? 0 : SEARCH_CLOSE_MS);
      return () => clearTimeout(id);
    }
    Animated.timing(expand, {
      toValue: 0,
      duration: reducedMotion ? 0 : SEARCH_CLOSE_MS,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (finished) setMounted(false);
    });
  }, [open, mounted, reducedMotion, expand]);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleToggle = () => {
    cancelPendingClose();
    if (open) {
      setValue('');
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  // Collapse back to the magnifier when focus leaves an empty field. Deferred a
  // beat so a click on the toggle (which blurs the input first) can cancel it
  // and avoid a close/re-open race. A non-empty query stays open.
  const handleBlur = () => {
    if (value) return;
    cancelPendingClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const webClipStyle: ViewStyle = {
    width: expanded ? INLINE_SEARCH_WIDTH : 0,
    opacity: expanded ? 1 : 0,
    transitionProperty: 'width, opacity',
    transitionDuration: reducedMotion ? '0ms' : `${open ? SEARCH_OPEN_MS : SEARCH_CLOSE_MS}ms`,
    transitionTimingFunction: SEARCH_EASING,
  } as unknown as ViewStyle;

  const nativeClipStyle = {
    width: expand.interpolate({ inputRange: [0, 1], outputRange: [0, INLINE_SEARCH_WIDTH] }),
    opacity: expand,
  };

  return (
    <HStack spacing="xs" align="center">
      <Animated.View style={[styles.inlineSearchClip, isWeb ? webClipStyle : nativeClipStyle]}>
        {mounted ? (
          <Animated.View style={styles.inlineSearchField}>
            <SearchInput
              value={value}
              onChangeText={setValue}
              placeholder={t('topbar.searchPlaceholder')}
              onClear={() => setValue('')}
              onBlur={handleBlur}
              autoFocus
              compact
            />
          </Animated.View>
        ) : null}
      </Animated.View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={open ? 'Close search' : 'Search'}
        accessibilityState={{ expanded: open }}
        onPress={handleToggle}
        style={styles.iconButton}
        hitSlop={4}
      >
        {open ? (
          <X size={18} color={colors.text.secondary} strokeWidth={1.8} />
        ) : (
          <Search size={18} color={colors.text.secondary} strokeWidth={1.8} />
        )}
      </Pressable>
    </HStack>
  );
}

export function DashboardHeader() {
  const { isDesktop } = useResponsive();
  const { openMobileDrawer, openNotificationFeed, notificationFeedAvailable } = useLayoutChrome();
  const { t } = useTranslation();
  const [feedOpen, setFeedOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchEnabled = isHeaderSearchEnabled();

  // On native mobile the layout owns the notification drawer (mirrors the
  // burger menu). On web the layout doesn't register an opener, so we
  // keep using the Sheet Modal here.
  const handleOpenFeed = () => {
    if (notificationFeedAvailable) openNotificationFeed();
    else setFeedOpen(true);
  };

  // Burger + page title render whenever the layout is in drawer mode
  // (mobile + tablet). Must stay aligned with DashboardLayout's
  // `showDesktopLayout = isDesktop` decision so the affordance for opening
  // the drawer always exists at tablet widths.
  const showDrawerChrome = !isDesktop;

  const leading = showDrawerChrome ? (
    <Pressable
      testID="nav_drawer_button"
      accessibilityRole="button"
      accessibilityLabel={t('layout.sidebar.expandSidebar')}
      onPress={openMobileDrawer}
      style={styles.iconButton}
      hitSlop={8}
    >
      <Menu size={20} color={colors.text.primary} strokeWidth={1.8} />
    </Pressable>
  ) : null;

  return (
    <>
      <Header
        showTopInset={false}
        height={isDesktop ? 72 : 56}
        style={styles.chrome}
        leading={leading}
        title={showDrawerChrome ? <HeaderTitle /> : null}
        trailing={
          <HStack spacing="md" align="center">
            <WorkspaceAvatar showDetails={isDesktop} />
            {searchEnabled && isDesktop && <InlineExpandingSearch />}
            {searchEnabled && !isDesktop && (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Search"
                onPress={() => setSearchOpen(true)}
                style={[styles.iconButton, styles.searchSpacing]}
                hitSlop={4}
              >
                <Search size={18} color={colors.text.secondary} strokeWidth={1.8} />
              </Pressable>
            )}
            <NotificationBell onPress={handleOpenFeed} />
          </HStack>
        }
      />
      {searchEnabled && !isDesktop && (
        <MobileSearchOverlay visible={searchOpen} onClose={() => setSearchOpen(false)} />
      )}
      {!notificationFeedAvailable && (
        <NotificationFeedSheet visible={feedOpen} onClose={() => setFeedOpen(false)} />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  // Header, sidebar and content are one uninterrupted surface, so this
  // overrides the Header primitive's own raised chrome fill down to the app
  // background. The primitive's hairline is dropped here rather than in the
  // primitive, which other screens still rely on.
  chrome: {
    backgroundColor: colors.background.primary,
    borderBottomWidth: 0,
  },
  title: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: tokens.fontSize.xlPlus,
    fontWeight: tokens.fontWeight.bold,
    color: colors.text.primary,
    letterSpacing: tokens.letterSpacing.tight,
    // Sora's tall caps clip at the top of the fixed-height nav header without
    // explicit line-box headroom (~1.25x).
    lineHeight: 28,
  },
  iconButton: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.navChrome,
  },
  // HStack gap is dropped on iOS via Box.supportsGap, so the trailing icons
  // render touching each other. Native-only marginRight restores the visual
  // gap without changing web layout (web honors the HStack gap natively).
  searchSpacing: Platform.select({
    ios: { marginRight: tokens.spacing.sm },
    android: { marginRight: tokens.spacing.sm },
    default: {},
  }),
  // Clip pinned to the right so the fixed-width field reveals magnifier -> left.
  // No borderRadius here: a rounded clip + overflow:hidden shaves the field's
  // own 1px border at the corners. The field's bounding box is square, so a
  // square clip leaves its rounded teal border fully intact.
  inlineSearchClip: {
    overflow: 'hidden',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  inlineSearchField: {
    width: INLINE_SEARCH_WIDTH,
    // Vertical breathing room so the field's border never sits flush against
    // the clip's overflow edge (which would shave the top/bottom border).
    paddingVertical: tokens.spacing.xs,
  },
});
